import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight, ShieldCheck, Truck, FileWarning, GraduationCap, ClipboardCheck,
  MessageSquareWarning, Megaphone, Printer, ChevronDown, ChevronRight,
} from "lucide-react";
import { EditableContentProvider, E } from "@/components/EditableText";
import { useSectionTracking } from "@/lib/useSectionTracking";
import AlaCartePricing from "@/components/guardian/AlaCartePricing";
import { SamplesSection } from "@/components/core/SamplesSection";
import { TeamSection } from "@/components/core/TeamSection";
import { CoreContact } from "@/components/core/CoreContact";

/* ============================================================================
 * Core Presentation — "You build. We keep you compliant."
 * Default microsite for all standard prospects (/p/<slug>). Portfolio-style
 * scrolling page: design your own program, see live samples, price it,
 * and send your ideal engagement to Colin.
 * ========================================================================== */

const CREAM = "#FDFBF5";
const CREAM_DEEP = "#FBF4DC";
const INK = "#0F1E2E";
const SUN = "#F5B400";
const SUN_DEEP = "#C98F00";
const EMBER = "#FF7B1F";

/* Module catalog for the core "design your own program" calculator. */
const CORE_MODULES = [
  {
    key: "vendor",
    label: "Vendor management — onboarding & monitoring",
    price: 500,
    footnote: "Includes up to 40 business screenings / month. Additional business screenings $8 each.",
  },
  {
    key: "aml",
    label: "OFAC / watchlist screening & monitoring",
    price: 450,
    footnote: "Includes up to 100 individual screenings + continuous monitoring / month. Additional screenings $3 each.",
  },
  {
    key: "sar",
    label: "SAR / UAR support",
    price: 450,
    footnote: "For fintechs partnering with a bank — alert triage, investigation workflow, UAR drafting, filing calendar.",
  },
  {
    key: "monitoring",
    label: "Monitoring & testing",
    price: 500,
    footnote: "Random-sample monitoring, control testing, and written recommendations.",
  },
  {
    key: "complaints",
    label: "Complaint management (UDAAP)",
    price: 350,
    footnote: null,
  },
  {
    key: "marketing",
    label: "Marketing review — self-directed + expert escalation",
    price: 350,
    footnote: "AI self-review on every asset; one click routes it to a human expert.",
  },
];

const CAPABILITIES = [
  {
    Icon: Truck,
    t: "Vendor management",
    b: "Onboarding and ongoing monitoring. New vendors screened, tiered, and re-screened on a schedule — OFAC, adverse media, SOC 2 attestations, remediation tracking.",
  },
  {
    Icon: FileWarning,
    t: "SAR / UAR support",
    b: "Fintech partnering with a bank? We run alert triage and investigations, and draft the Suspicious or Unusual Activity Reports your partner bank expects — on their calendar.",
  },
  {
    Icon: GraduationCap,
    t: "Customized training with tracking & alerts",
    b: "Training built for your products and states — with completion tracking, alerts when someone falls behind, and audit-ready attestation logs.",
  },
  {
    Icon: ClipboardCheck,
    t: "Monitoring & testing",
    b: "Random-sample monitoring and control testing with written recommendations — so findings come from us before they come from your examiner.",
  },
  {
    Icon: MessageSquareWarning,
    t: "Complaint management",
    b: "Intake, tagging, root-cause trending, and UDAAP escalation — the paper trail regulators ask for first.",
  },
  {
    Icon: Megaphone,
    t: "Marketing reviews — self-directed, expert on call",
    b: "Run the AI review on every asset yourself. Anything borderline, one click shoots it to a compliance expert for a human read.",
  },
];

const TRACKED_SECTIONS = ["hero", "program", "samples", "pricing", "team", "contact"];

const CORE_PRICING_COPY = {
          headline: "Design your ideal engagement. Watch the price build live.",
          subhead: "Toggle the modules that match your risk. Add fractional experts by the hour. Everything reprices instantly — save the configuration and it goes straight to Colin.",
          coreTitle: "Guardian Core",
          companiesStep: "Step 3 · Entities · Volume discount",
          companiesTitle: "How many entities?",
          companiesNote: "Most engagements start at 1. Running multiple brands, subsidiaries, or programs? The volume discount applies to each entity's stack.",
          unitLabel: "entities",
          teamStep: "Step 4 · Build your team · Shared across your program",
          teamIntro: "One fractional team across your whole program — not per entity.",
          teamSubtotalLabel: "Team subtotal (program-wide, not multiplied by entities)",
          individualLabel: "Single entity",
          portfolioLabel: "Multiple entities",
          individualNote: "Pricing a single entity — the full stack applies with no volume discount. Running multiple brands, subsidiaries, or programs? Switch to Multiple entities to see the volume tiers.",
        };

export default function CoreProposal({ prospect = null, editMode = false }) {
  const { seenSections, engagementPct } = useSectionTracking(TRACKED_SECTIONS);
  const quoteExtras = useMemo(() => ({ source: "core", prospect_slug: prospect?.slug || null }), [prospect?.slug]);
  const companyName = prospect?.company_name || "Your Company";

  const jumpTo = (id) => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const handlePrint = () => window.print();

  return (
    <EditableContentProvider ns="core" editMode={editMode}>
    <div
      data-testid="core-proposal"
      className="min-h-screen w-full antialiased text-ink-900"
      style={{ background: `linear-gradient(180deg, ${CREAM} 0%, ${CREAM_DEEP} 100%)`, color: INK }}
    >
      <style>{`
        .pcp-h { font-family: 'Source Serif Pro', 'Source Serif 4', Georgia, serif; font-weight: 300; letter-spacing: -0.02em; line-height: 1.05; }
        .pcp-mono { font-family: 'JetBrains Mono', ui-monospace, monospace; letter-spacing: 0.18em; text-transform: uppercase; }
        .pcp-card { background: rgba(255,255,255,0.7); border: 1px solid rgba(15,30,46,0.08); border-radius: 14px; backdrop-filter: blur(6px); }
        .pcp-stripe { background: repeating-linear-gradient(135deg, rgba(245,180,0,0.10) 0, rgba(245,180,0,0.10) 12px, transparent 12px, transparent 24px); }
        @keyframes pcpBounce {
          0%, 100% { transform: translateY(0); }
          50%      { transform: translateY(6px); }
        }
        @media print { .no-print { display: none !important; } }
      `}</style>

      {/* Sticky header */}
      <header className="sticky top-0 z-40 backdrop-blur-md no-print" style={{ background: "rgba(253, 251, 245, 0.85)", borderBottom: "1px solid rgba(15,30,46,0.06)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <div className="text-base font-medium" style={{ color: INK }}>CompliSun</div>
              <div className="pcp-mono text-[9px]" style={{ color: SUN_DEEP }}>Prepared for {companyName}</div>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => jumpTo("program")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-program">Program</button>
            <button onClick={() => jumpTo("samples")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-samples">Samples</button>
            <button onClick={() => jumpTo("pricing")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-pricing">Pricing</button>
            <button onClick={() => jumpTo("team")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-team">Team</button>
            <button onClick={() => jumpTo("contact")} className="pcp-mono text-[10px] hover:opacity-100 opacity-60 transition" data-testid="nav-contact">Contact</button>
            <button
              onClick={() => jumpTo("pricing")}
              className="pcp-mono text-[10px] px-4 py-2 rounded-md transition hover:translate-y-[-1px]"
              style={{ background: INK, color: CREAM }}
              data-testid="nav-cta"
            >Design your program <ChevronRight className="inline h-3 w-3 ml-1" /></button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section id="hero" className="relative px-6 md:px-12 pt-14 md:pt-24 pb-20 md:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute -top-32 -right-40 h-[600px] w-[600px] rounded-full blur-3xl opacity-50" style={{ background: `radial-gradient(circle, ${SUN} 0%, transparent 70%)` }} />
        <div className="pointer-events-none absolute -bottom-40 -left-32 h-[500px] w-[500px] rounded-full blur-3xl opacity-25" style={{ background: `radial-gradient(circle, ${EMBER} 0%, transparent 70%)` }} />
        <div className="relative max-w-6xl mx-auto">
          <p className="pcp-mono text-[11px] mb-6" style={{ color: SUN_DEEP }}>
            / Enterprise compliance & risk · operationalized
          </p>
          <h1 className="pcp-h text-5xl md:text-7xl lg:text-[88px] mb-8 max-w-5xl" data-testid="core-hero-headline">
            <E k="hero.headline" defaultText="You build. We keep you compliant." as="span" multiline />
          </h1>
          <p className="max-w-3xl text-lg md:text-xl leading-relaxed opacity-80 mb-10">
            <E
              k="hero.body"
              defaultText="CompliSun turns enterprise compliance and risk into an operating system — vendor management, SAR/UAR support, customized training, monitoring & testing, complaint management, and marketing review — run by people who've done this inside real banks and fintechs. Design the exact program you need below and watch the cost build in real time."
              as="span"
              multiline
            />
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => jumpTo("pricing")}
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-md font-medium transition hover:translate-y-[-1px]"
              style={{ background: INK, color: CREAM }}
              data-testid="hero-cta-primary"
            >
              Design your program <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" />
            </button>
            <button
              onClick={() => jumpTo("samples")}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-md font-medium border transition"
              style={{ borderColor: "rgba(15,30,46,0.15)", color: INK, background: "rgba(255,255,255,0.6)" }}
              data-testid="hero-cta-secondary"
            >
              Try the live samples
            </button>
            <button
              onClick={handlePrint}
              className="inline-flex items-center gap-2 px-5 py-4 rounded-md pcp-mono text-[10px] opacity-60 hover:opacity-100 transition no-print"
              data-testid="hero-print"
            >
              <Printer className="h-3.5 w-3.5" /> Print / save PDF
            </button>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
            {[
              { k: "You design it", v: "Pick modules, team, and hours — à la carte" },
              { k: "We operationalize it", v: "Automation first, experts embedded" },
              { k: "Detroit-rooted", v: "Attorney-led" },
              { k: "Predictable costs", v: "No surprise compliance bills" },
            ].map((t) => (
              <div key={t.k} className="pcp-card p-4">
                <div className="pcp-mono text-[9px]" style={{ color: SUN_DEEP }}>{t.k}</div>
                <div className="text-sm font-medium mt-1">{t.v}</div>
              </div>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => jumpTo("program")}
          data-testid="hero-scroll-down"
          aria-label="Scroll to next section"
          className="no-print absolute left-1/2 -translate-x-1/2 bottom-6 md:bottom-8 inline-flex flex-col items-center gap-1 opacity-65 hover:opacity-100 transition"
          style={{ color: INK }}
        >
          <span className="pcp-mono text-[9px]" style={{ letterSpacing: "0.24em", color: SUN_DEEP }}>SCROLL</span>
          <span
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border"
            style={{ borderColor: "rgba(15,30,46,0.20)", background: "rgba(255,255,255,0.55)", animation: "pcpBounce 2.2s ease-in-out infinite" }}
          >
            <ChevronDown className="h-4 w-4" strokeWidth={1.8} />
          </span>
        </button>
      </section>

      {/* PROGRAM — what we operationalize */}
      <section id="program" className="px-6 md:px-12 py-16 md:py-24" style={{ background: "rgba(255,255,255,0.4)", borderTop: "1px solid rgba(15,30,46,0.06)", borderBottom: "1px solid rgba(15,30,46,0.06)" }}>
        <div className="max-w-6xl mx-auto">
          <p className="pcp-mono text-[11px] mb-4" style={{ color: SUN_DEEP }}>/ The program · pick what you need</p>
          <h2 className="pcp-h text-4xl md:text-5xl mb-6 max-w-4xl">
            <E k="program.headline" defaultText="Six ways compliance quietly eats your week. We run all of them." as="span" multiline />
          </h2>
          <p className="text-base md:text-lg leading-relaxed opacity-85 max-w-3xl mb-10">
            <E
              k="program.intro"
              defaultText="Every module below is a working system — automation plus a named human who owns it. Take one, take all six, or start small and add as you grow. And with all of it, one click shoots anything to an expert."
              as="span"
              multiline
            />
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CAPABILITIES.map(({ Icon, t, b }, i) => (
              <div key={t} className="pcp-card p-5" data-testid={`capability-card-${i}`}>
                <Icon className="h-5 w-5 mb-3" style={{ color: SUN_DEEP }} strokeWidth={1.6} />
                <div className="text-base font-semibold mb-2" style={{ color: INK }}>
                  <E k={`program.cap${i + 1}.t`} defaultText={t} as="span" />
                </div>
                <p className="text-sm opacity-75 leading-relaxed">
                  <E k={`program.cap${i + 1}.b`} defaultText={b} as="span" multiline />
                </p>
              </div>
            ))}
          </div>

          <div className="pcp-card p-5 mt-8 pcp-stripe" style={{ borderLeft: `4px solid ${SUN}` }}>
            <div className="pcp-mono text-[10px] mb-2" style={{ color: SUN_DEEP }}>/ Shoot it to an expert</div>
            <p className="text-sm md:text-base opacity-85">
              <E
                k="program.expert"
                defaultText="Everything above is self-directed by default — and everything has an escalation path. A marketing asset you're unsure about, a screening hit, a complaint that smells like UDAAP: one click routes it to a CompliSun expert who's handled it before."
                as="span"
                multiline
              />
            </p>
          </div>
        </div>
      </section>

      {/* SAMPLES — interactive demos */}
      <SamplesSection />

      {/* PRICING — design your own program */}
      <AlaCartePricing
        modules={CORE_MODULES}
        defaultCompanies={1}
        copy={CORE_PRICING_COPY}
        quoteExtras={quoteExtras}
      />

      {/* TEAM — full roster */}
      <TeamSection />

      {/* CONTACT — send your ideal engagement */}
      <CoreContact prospect={prospect} engagementPct={engagementPct} seenSections={Array.from(seenSections)} />

      {/* Footer */}
      <footer className="px-6 md:px-12 py-12 mt-8" style={{ borderTop: "1px solid rgba(15,30,46,0.08)" }}>
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain" />
            <div className="leading-tight">
              <div className="text-sm font-medium">CompliSun</div>
              <div className="pcp-mono text-[9px] opacity-50">Detroit-rooted · Attorney-led</div>
            </div>
          </div>
          <div className="pcp-mono text-[10px] opacity-50">© {new Date().getFullYear()} CompliSun · Enterprise compliance & risk, operationalized · Engagement {engagementPct}%</div>
        </div>
      </footer>
    </div>
    </EditableContentProvider>
  );
}
